import { unlinkProvider } from "@/app/actions/auth/signup";
import { createClient } from "@/lib/supabase/client";
import { SiGithub, SiGoogle } from "@icons-pack/react-simple-icons";
import { EnvelopeClosedIcon } from "@radix-ui/react-icons";
import { Flex, Grid } from "@radix-ui/themes";
import { Provider, User, UserIdentity } from "@supabase/supabase-js";
import { Route } from "next";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";

const availableProviders: Provider[] = ["google", "github"];

export default function Providers({ user }: { user?: User | null }) {
  const supabase = createClient();

  const [activeIdentities, setActiveIdentities] = useState<UserIdentity[]>([]);
  const router = useRouter();

  useEffect(() => {
    const checkIdentities = async () => {
      if (user) {
        const { data, error } = await supabase.auth.getUserIdentities();
        if (error) {
          console.log(error);
          return;
        }

        if (!data) {
          return;
        }

        setActiveIdentities(data.identities);
        console.log(data.identities);
      }
    };

    (async () => checkIdentities())();
  }, [supabase.auth, user]);

  const unlinkAccount = async (provider: UserIdentity) => {
    const { error } = await unlinkProvider(provider);
    if (error) {
      console.log(error);
      return;
    }
  };

  const linkAccount = async (provider: Provider) => {
    const redirectPath = encodeURIComponent("/settings");
    router.push(
      `/auth/oauth/${provider}/link?redirectPath=${redirectPath}` as Route,
    );
  };

  const unusedProviders = availableProviders.filter(
    (provider) =>
      !activeIdentities.some((identity) => identity.provider === provider),
  );

  return (
    <Card id="linkedAccounts">
      <CardHeader>
        <CardTitle>Linked Accounts</CardTitle>
        <CardDescription>Link and unlink accounts</CardDescription>
      </CardHeader>
      <CardContent>
        {user && activeIdentities.length === 0 && (
          <p>You have no linked accounts</p>
        )}
        {/* add ability to link accounts as well as unlink them */}
        <Grid columns="2" gap="4">
          {activeIdentities.map((account) => (
            <Card key={account.id}>
              <CardHeader>
                <CardTitle>
                  {account.provider === "google" && (
                    <Flex gap="2" align="center">
                      <SiGoogle />
                      <span className="ml-2">Google</span>
                    </Flex>
                  )}
                  {account.provider === "github" && (
                    <Flex gap="2" align="center">
                      <SiGithub />
                      <span className="ml-2">GitHub</span>
                    </Flex>
                  )}
                  {account.provider === "email" && (
                    <Flex gap="2" align="center">
                      <EnvelopeClosedIcon />
                      <span className="ml-2">Email</span>
                    </Flex>
                  )}
                </CardTitle>
                <CardDescription>{account.id}</CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => unlinkAccount(account)}
                >
                  Unlink
                </Button>
              </CardContent>
            </Card>
          ))}
          {unusedProviders.map((provider) => (
            <Card key={provider}>
              <CardHeader>
                <CardTitle>
                  {provider === "google" && (
                    <Flex gap="2" align="center">
                      <SiGoogle />
                      <span className="ml-2">Google</span>
                    </Flex>
                  )}
                  {provider === "github" && (
                    <Flex gap="2" align="center">
                      <SiGithub />
                      <span className="ml-2">GitHub</span>
                    </Flex>
                  )}
                  {/* {provider === "email" && (
                    <Flex gap="2" align="center">
                      <EnvelopeClosedIcon />
                      <span className="ml-2">Email</span>
                    </Flex>
                  )} */}
                </CardTitle>
                <CardDescription>&nbsp;</CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  type="button"
                  variant="default"
                  onClick={() => linkAccount(provider)}
                >
                  Link
                </Button>
              </CardContent>
            </Card>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
}
